import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { IMAGES, LOGO_URL, PHONE, PHONE_HREF, LICENSE } from "@/data/site";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About - McMullen Construction Co. | Florida General Contractor"
        description="McMullen Construction Co. is a licensed Florida general contractor (CGC #1533095) focused on one project at a time - remodels and new construction done personally."
      />
      <PageHero
        testId="about-hero"
        eyebrow="Who We Are"
        lines={["A contractor who treats", "your home like a story."]}
        intro="McMullen Construction Co. is a licensed Florida general contractor focused on remodels and new construction - personalized, precise, and built to last."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="about-story-section">
        <div className="lg:col-span-7 space-y-8">
          <Reveal>
            <blockquote className="border-l-2 border-[#D4AF37] pl-6 font-display text-xl sm:text-2xl italic leading-relaxed text-[#C9C7C2]" data-testid="about-dream-quote">
              Building a custom home or remodeling your existing home is like weaving a dream into reality. From the foundation to the rooftop, every detail is a brushstroke of your imagination, coming alive with each decision and design.
            </blockquote>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm sm:text-base leading-relaxed text-[#A1A1AA]">
              The walls become the canvas where memories are painted, and the rooms are chapters waiting to be written. Every corner holds the promise of comfort, warmth, and laughter. The windows invite the sun to illuminate your aspirations, and the doors welcome loved ones into a sanctuary crafted with care. As we build your dream home, remember that every choice you make shapes the story of your life - creating a haven where dreams truly come true.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-sm sm:text-base leading-relaxed text-[#A1A1AA]">
              We are a contractor that focuses on each individual project; this is an asset because it allows for personalized solutions that meet the unique needs of our clients. This approach ensures attention to detail, resulting in superior craftsmanship and customer satisfaction. Additionally, it fosters strong client relationships, leading to repeat business and positive referrals.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/gallery"
              data-testid="about-gallery-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#F3E081] transition-colors"
            >
              See the craftsmanship <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="bg-[#121216]/90 border border-[rgba(212,175,55,0.25)] p-8" data-testid="about-license-card">
            <img src={LOGO_URL} alt="McMullen Construction Co. logo" className="w-full rounded-sm" />
            <div className="hairline-gold mt-8 pt-6 space-y-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-[#F4F2EE]">State Certified General Contractor</p>
                  <p className="font-mono-accent text-xs tracking-[0.15em] text-[#D4AF37] mt-1" data-testid="about-license-number">{LICENSE}</p>
                </div>
              </div>
              <a href={PHONE_HREF} data-testid="about-phone-link" className="block font-mono-accent text-sm tracking-[0.15em] text-[#F3E081] hover:text-[#D4AF37] transition-colors">
                {PHONE}
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-ink-2 border-y border-[rgba(212,175,55,0.15)] py-16 sm:py-24" data-testid="about-photos-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Recent Work</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[IMAGES.buildBlock, IMAGES.sunroom, IMAGES.bathConcrete].map((src, i) => (
              <Reveal key={src} delay={i * 0.1}>
                <div className="gold-frame-offset">
                  <img src={src} alt="McMullen Construction project photo" loading="lazy" className="relative z-10 h-64 w-full object-cover img-spotlight" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-20 sm:py-28 text-center" data-testid="about-cta-section">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#F3E081]">Let's write your home's next chapter.</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="mt-9 inline-block bg-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold text-black hover:bg-[#F3E081] transition-colors duration-300"
          >
            Start Your Project
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
